import { MarketStatus } from '../enums/market-status.enum';

export const MATCH_QUESTION_RESULT_FILTERS = ['beat', 'below', 'pending'] as const;
export type MatchQuestionResultFilter = (typeof MATCH_QUESTION_RESULT_FILTERS)[number];

export type MatchQuestionFilterMarket = {
	question: string;
	status: MarketStatus;
	prediction?: number | null;
	relative_brier_score?: number | null;
	category_id?: string | null;
	category_name?: string | null;
};

export type MatchQuestionCategory = {
	id: string;
	name: string;
};

export type MatchQuestionResultCounts = {
	beat: number;
	below: number;
	pending: number;
};

export const getMatchQuestionResultBucket = (
	market: MatchQuestionFilterMarket
): MatchQuestionResultFilter | 'tied' | null => {
	// Result buckets only cover questions the user actually predicted.
	if (market.prediction == null) {
		return null;
	}

	// Refunded questions are done — they are not "waiting for a result".
	if (market.status === MarketStatus.Void) {
		return null;
	}

	if (market.status !== MarketStatus.Settled) {
		return 'pending';
	}

	if (market.relative_brier_score == null) {
		return null;
	}

	if (market.relative_brier_score > 0) return 'beat';
	if (market.relative_brier_score < 0) return 'below';
	return 'tied';
};

export const getMatchQuestionResultCounts = (
	markets: MatchQuestionFilterMarket[]
): MatchQuestionResultCounts => {
	return markets.reduce<MatchQuestionResultCounts>(
		(acc, market) => {
			const bucket = getMatchQuestionResultBucket(market);
			if (bucket === 'beat' || bucket === 'below' || bucket === 'pending') {
				acc[bucket] += 1;
			}
			return acc;
		},
		{ beat: 0, below: 0, pending: 0 }
	);
};

export const getMatchQuestionCategories = (
	markets: MatchQuestionFilterMarket[]
): MatchQuestionCategory[] => {
	const byId = new Map<string, string>();

	for (const market of markets) {
		if (!market.category_id || !market.category_name) continue;
		if (!byId.has(market.category_id)) {
			byId.set(market.category_id, market.category_name);
		}
	}

	return Array.from(byId.entries())
		.map(([id, name]) => ({ id, name }))
		.sort((a, b) => a.name.localeCompare(b.name));
};

export const matchesMatchQuestionSearch = (question: string, query?: string | null): boolean => {
	const normalizedQuery = query?.trim().toLowerCase() ?? '';
	if (!normalizedQuery) return true;

	const haystack = question.toLowerCase();
	const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
	return tokens.every((token) => haystack.includes(token));
};

export const filterMatchQuestions = <T extends MatchQuestionFilterMarket>(
	markets: T[],
	options: {
		search?: string | null;
		resultFilter?: MatchQuestionResultFilter | null;
		categoryId?: string | null;
	}
): T[] => {
	const { search, resultFilter, categoryId } = options;

	return markets.filter((market) => {
		if (!matchesMatchQuestionSearch(market.question, search)) {
			return false;
		}

		if (categoryId && market.category_id !== categoryId) {
			return false;
		}

		if (resultFilter) {
			return getMatchQuestionResultBucket(market) === resultFilter;
		}

		return true;
	});
};
